import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { GamificationEventStatus, GamificationPeriod, GamificationRuleKey } from '@troia-v3/types';

import { useTheme } from '@/contexts/theme';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { FilterChip, FiltersButton, OperatorAvatarFilter, ToolbarSearch } from '@/shared/components/ui';
import { useEscapeKey } from '@/shared/components/ui/Modal';
import { formatPeriodLabel, previousPeriodKeys } from '@/shared/utils/gamificationPeriod';

export interface AuditFilterState {
  status?: GamificationEventStatus;
  ruleKey?: GamificationRuleKey;
  periodKey?: string;
  userId?: string;
  search: string;
}

export const STATUS_LABEL: Record<GamificationEventStatus, string> = { pending: 'pendente', confirmed: 'confirmado', reversed: 'estornado' };
export const RULE_LABEL: Record<GamificationRuleKey, string> = { lead_captured: 'Lead captado', form_completed: 'Formulário preenchido', lead_won: 'Venda fechada' };

interface Props { value: AuditFilterState; onChange: (v: AuditFilterState) => void; period: GamificationPeriod; currentPeriodKey: string }

const POPOVER_WIDTH = 256;

/* Opção do popover — só tokens; o container do popover sobrescreve os tokens pro tema claro. */
const Option: React.FC<{ active: boolean; onClick: () => void; testId: string; children: React.ReactNode }> = ({ active, onClick, testId, children }) => (
  <button type="button" onClick={onClick} className="w-full text-left px-2.5 h-9 rounded-lg text-[13px] hover:bg-[var(--theme-surface-sunken)]" style={{ color: active ? 'var(--theme-primary)' : 'var(--theme-text)', fontWeight: active ? 600 : 500, backgroundColor: active ? 'var(--theme-primary-background)' : undefined }} data-testid={testId}>
    {children}
  </button>
);

/**
 * Linha de filtros da auditoria (mockup 10 B): Filtros ▸ avatares ▸ chips ▸ Limpar tudo ▸ busca.
 *
 * Popover = dropdown ⇒ (1) **portal em `document.body`** com `position: fixed` ancorado no botão
 * (regra dura de overlays do DS — dentro do scroll das abas ele ficava cortado e esticava o
 * conteúdo da aba), `z-[100]` (camada de popovers), altura limitada à janela com rolagem própria;
 * (2) **tema claro fixo** pela técnica do `PeriodPicker`/`Modal`: o container sobrescreve as CSS
 * variables com os tokens claros e os filhos seguem consumindo `var(--theme-*)` — nunca hex solto.
 */
export const AuditFilters: React.FC<Props> = ({ value, onChange, period, currentPeriodKey }) => {
  const { lightTokens } = useTheme();
  const lt = lightTokens.colors;
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 0 });
  const { data: users = [], isLoading } = useUsersDropdown({ status: 'active' });
  const periods = [currentPeriodKey, ...previousPeriodKeys(currentPeriodKey, 5)];

  const toggle = (): void => {
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const top = rect.bottom + 8;
      setPos({
        top,
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - POPOVER_WIDTH - 8)),
        maxHeight: Math.max(160, window.innerHeight - top - 16),
      });
    }
    setOpen((v) => !v);
  };

  // Esc fecha o popover — mesmo helper dos outros overlays do DS (`Modal`, `PeriodPicker`).
  // Clicar fora fecha pelo backdrop (política do dono para menus/popovers).
  useEscapeKey(() => setOpen(false), open);

  const count = [value.status, value.ruleKey, value.periodKey].filter(Boolean).length;
  const set = (p: Partial<AuditFilterState>) => onChange({ ...value, ...p });

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="gamification-audit-filterbar">
      <div ref={anchorRef}>
        <FiltersButton count={count} active={open} onClick={toggle} data-testid="gamification-audit-filters" />
      </div>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} data-testid="gamification-audit-filters-backdrop" />
          <div
            role="dialog"
            aria-label="Filtros da auditoria"
            className="fixed z-[100] rounded-xl p-2 space-y-2 overflow-y-auto"
            style={{
              top: pos.top,
              left: pos.left,
              width: POPOVER_WIDTH,
              maxHeight: pos.maxHeight,
              backgroundColor: lt.background.card,
              border: `1px solid ${lt.border.strong}`,
              boxShadow: 'var(--shadow-dropdown)',
              // Override das CSS variables pro tema claro (popover = dropdown, sempre light)
              '--theme-text': lt.text.primary,
              '--theme-muted-text': lt.text.muted,
              '--theme-primary': lt.primary,
              '--theme-primary-background': lt.primaryLight,
              '--theme-surface-sunken': '#f1f5f9',
            } as React.CSSProperties}
            data-testid="gamification-audit-filters-popover"
          >
            <div className="text-[11px] font-bold uppercase tracking-wider px-2.5 pt-1" style={{ color: 'var(--theme-muted-text)' }}>Status</div>
            {(Object.keys(STATUS_LABEL) as GamificationEventStatus[]).map((s) => (
              <Option key={s} active={value.status === s} onClick={() => set({ status: value.status === s ? undefined : s })} testId={`gamification-audit-filter-status-${s}`}>{STATUS_LABEL[s]}</Option>
            ))}
            <div className="text-[11px] font-bold uppercase tracking-wider px-2.5 pt-1" style={{ color: 'var(--theme-muted-text)' }}>Evento</div>
            {(Object.keys(RULE_LABEL) as GamificationRuleKey[]).map((r) => (
              <Option key={r} active={value.ruleKey === r} onClick={() => set({ ruleKey: value.ruleKey === r ? undefined : r })} testId={`gamification-audit-filter-rule-${r}`}>{RULE_LABEL[r]}</Option>
            ))}
            <div className="text-[11px] font-bold uppercase tracking-wider px-2.5 pt-1" style={{ color: 'var(--theme-muted-text)' }}>Período</div>
            {periods.map((k) => (
              <Option key={k} active={(value.periodKey ?? currentPeriodKey) === k} onClick={() => set({ periodKey: k === currentPeriodKey ? undefined : k })} testId={`gamification-audit-filter-period-${k}`}>{formatPeriodLabel(period, k)}</Option>
            ))}
          </div>
        </>,
        document.body,
      )}

      <OperatorAvatarFilter
        operators={users.map((u) => ({ id: u.id, name: u.name, ...(u.avatar ? { avatar: u.avatar } : {}) }))}
        selectedId={value.userId ?? null}
        onSelect={(id) => set({ userId: id ?? undefined })}
        loading={isLoading}
        data-testid="gamification-audit-operators"
      />

      {value.status && <FilterChip label="Status" value={STATUS_LABEL[value.status]} onClick={toggle} onRemove={() => set({ status: undefined })} />}
      {value.ruleKey && <FilterChip label="Evento" value={RULE_LABEL[value.ruleKey]} onClick={toggle} onRemove={() => set({ ruleKey: undefined })} />}
      {value.periodKey && <FilterChip label="Período" value={formatPeriodLabel(period, value.periodKey)} onClick={toggle} onRemove={() => set({ periodKey: undefined })} />}
      {(count > 0 || value.userId) && (
        <button type="button" className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} onClick={() => onChange({ search: value.search })} data-testid="gamification-audit-clear">Limpar tudo</button>
      )}

      <div className="ml-auto w-full sm:w-[260px]">
        <ToolbarSearch value={value.search} onChange={(search) => set({ search })} placeholder="Buscar lead ou vendedor…" data-testid="gamification-audit-search" />
      </div>
    </div>
  );
};
AuditFilters.displayName = 'AuditFilters';
